import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';

export interface StatusPresetStatus {
  value: string;
  name: string;
  color?: string | null;
  icon?: string | null;
  category?: 'open' | 'active' | 'closed';
}

export interface StatusPreset {
  id: string;
  orgId: string;
  name: string;
  statuses: StatusPresetStatus[];
  createdBy: string | null;
  createdAt: string;
}

export async function fetchStatusPresets(orgId: string): Promise<StatusPreset[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('status_presets')
      .select('*')
      .eq('org_id', orgId)
      .order('name', { ascending: true })
  );

  if (error) throw error;
  return (data || []).map(row => ({
    id: row.id,
    orgId: row.org_id,
    name: row.name,
    statuses: row.statuses as StatusPresetStatus[],
    createdBy: row.created_by,
    createdAt: row.created_at,
  }));
}

export async function createStatusPreset(
  orgId: string,
  name: string,
  statuses: StatusPresetStatus[],
  createdBy?: string,
): Promise<StatusPreset> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('status_presets')
      .insert({ org_id: orgId, name, statuses, created_by: createdBy || null })
      .select()
      .single()
  );

  if (error || !data) throw error || new Error('Failed to create preset');
  return {
    id: data.id,
    orgId: data.org_id,
    name: data.name,
    statuses: data.statuses as StatusPresetStatus[],
    createdBy: data.created_by,
    createdAt: data.created_at,
  };
}

export async function deleteStatusPreset(presetId: string): Promise<void> {
  const { error } = await withRetry(() =>
    supabase.from('status_presets').delete().eq('id', presetId)
  );
  if (error) throw error;
}
